import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Plus,
  Download,
  Pencil,
  Trash2,
  Copy,
  Star,
  Upload,
  Search,
  MoreVertical,
  Wand2,
  Settings,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import templateService from '../services/templateService';
import TemplateAnnotator from './TemplateAnnotator';
import ManualTemplateMapper from './ManualTemplateMapper';

const TemplateManager = ({ entityType, onTemplateSelect, dialogMode = false }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [annotatorTemplate, setAnnotatorTemplate] = useState(null);
  const [manualMapperOpen, setManualMapperOpen] = useState(false);
  const [manualMapperTemplate, setManualMapperTemplate] = useState(null);
  const [replaceFileDialogOpen, setReplaceFileDialogOpen] = useState(false);
  const [replaceFileTemplate, setReplaceFileTemplate] = useState(null);

  // Form states
  const [uploadFile, setUploadFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: '',
    entityType: entityType || 'sales',
    language: 'cn',
    isDefault: false,
    tags: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Replace file form states
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceChangeNotes, setReplaceChangeNotes] = useState('');
  const [replaceFormErrors, setReplaceFormErrors] = useState({});

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        entityType,
        language: selectedLanguage || undefined,
        search: searchTerm || undefined,
        page: page + 1, // API uses 1-based pagination
        pageSize
      };

      const response = await templateService.listTemplates(options);
      setTemplates(response.templates || []);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [entityType, selectedLanguage, searchTerm, page, pageSize]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0); // Reset to first page
  };

  const handleUpload = async () => {
    try {
      setSubmitting(true);
      setFormErrors({});

      // Validate form
      const errors = {};
      if (!formData.name.trim()) errors.name = 'Template name is required';
      if (!formData.templateId.trim()) errors.templateId = 'Template ID is required';
      if (!uploadFile) errors.file = 'Please select a template file';

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      // Validate file
      const fileErrors = templateService.validateTemplateFile(uploadFile);
      if (fileErrors.length > 0) {
        setFormErrors({ file: fileErrors.join(', ') });
        return;
      }

      const metadata = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        template_id: formData.templateId.trim(),
        entity_type: formData.entityType,
        language: formData.language,
        is_default: formData.isDefault,
        tags: formData.tags
      };

      await templateService.uploadTemplate(uploadFile, metadata);

      setUploadDialogOpen(false);
      resetForm();
      await loadTemplates();
    } catch (err) {
      console.error('Error uploading template:', err);
      setFormErrors({ submit: err.response?.data?.error || 'Failed to upload template' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (template) => {
    try {
      const { blob, filename } = await templateService.downloadTemplate(template.template_id);
      templateService.downloadBlob(blob, filename);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await templateService.setDefaultTemplate(template.template_id);
      await loadTemplates();
    } catch (err) {
      console.error('Error setting default template:', err);
      setError('Failed to set default template');
    }
  };

  const handleEdit = (template) => {
    // Open the manual mapper for editing metadata
    handleOpenManualMapper(template);
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${template.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Use hard delete (permanent deletion)
      await templateService.deleteTemplate(template.template_id, true);
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateId: '',
      entityType: entityType || 'sales',
      language: 'cn',
      isDefault: false,
      tags: []
    });
    setUploadFile(null);
    setFormErrors({});
  };

  const handleOpenAnnotator = (template) => {
    setAnnotatorTemplate(template);
    setAnnotatorOpen(true);
  };

  const handleOpenManualMapper = (template) => {
    setManualMapperTemplate(template);
    setManualMapperOpen(true);
  };

  const handleAnnotatorSave = async (metadata) => {
    try {
      await loadTemplates();
      console.log('Template metadata saved successfully');
    } catch (err) {
      console.error('Error saving template metadata:', err);
      setError('Failed to save template metadata');
    }
  };

  const handleManualMapperSave = async (metadata) => {
    try {
      await loadTemplates();
      console.log('Manual template mapping saved successfully');
    } catch (err) {
      console.error('Error saving manual template mapping:', err);
      setError('Failed to save manual template mapping');
    }
  };

  const handleOpenReplaceFile = (template) => {
    setReplaceFileTemplate(template);
    setReplaceFile(null);
    setReplaceChangeNotes('');
    setReplaceFormErrors({});
    setReplaceFileDialogOpen(true);
  };

  const handleReplaceFile = async () => {
    try {
      setSubmitting(true);
      setReplaceFormErrors({});

      // Validate file
      if (!replaceFile) {
        setReplaceFormErrors({ file: '请选择新的模板文件' });
        return;
      }

      const fileErrors = templateService.validateTemplateFile(replaceFile);
      if (fileErrors.errors && fileErrors.errors.length > 0) {
        setReplaceFormErrors({ file: fileErrors.errors.join(', ') });
        return;
      }

      // Replace template file (preserves metadata)
      await templateService.replaceTemplateFile(
        replaceFileTemplate.template_id,
        replaceFile,
        replaceChangeNotes || ''
      );

      setReplaceFileDialogOpen(false);
      setReplaceFileTemplate(null);
      setReplaceFile(null);
      setReplaceChangeNotes('');
      await loadTemplates();
    } catch (err) {
      console.error('Error replacing template file:', err);
      setReplaceFormErrors({ submit: err.message || '替换模板文件失败' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTemplateClick = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const TemplateCard = ({ template }) => (
    <Card
      className={`h-full ${onTemplateSelect ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={() => handleTemplateClick(template)}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-base font-semibold truncate flex-1 mr-2 flex items-center gap-2">
            {template.name}
            {template.is_default && (
              <Star className="h-4 w-4 text-warning fill-yellow-500" />
            )}
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleDownload(template);
              }}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetDefault(template);
                }}
                disabled={template.is_default}
              >
                <Star className="h-4 w-4 mr-2" />
                Set as Default
              </DropdownMenuItem>

              <DropdownMenuItem disabled onClick={(e) => e.stopPropagation()}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate (Coming Soon)
              </DropdownMenuItem>

              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleEdit(template);
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Metadata
              </DropdownMenuItem>

              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleOpenReplaceFile(template);
              }}>
                <Upload className="h-4 w-4 mr-2" />
                Replace File
              </DropdownMenuItem>

              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleOpenAnnotator(template);
              }}>
                <Wand2 className="h-4 w-4 mr-2" />
                Auto-Detect Mappings
              </DropdownMenuItem>

              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleOpenManualMapper(template);
              }}>
                <Settings className="h-4 w-4 mr-2" />
                Manual Mappings
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(template);
                }}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <p className="text-sm text-muted-foreground mb-4 min-h-[2.5em] line-clamp-2">
          {template.description || 'No description'}
        </p>

        <div className="flex gap-1 flex-wrap mb-4">
          <Badge variant="default">
            {templateService.getEntityDisplayName(template.entity_type)}
          </Badge>
          <Badge variant="outline">
            {templateService.getLanguageDisplayName(template.language)}
          </Badge>
          {template.is_default && (
            <Badge variant="secondary" className="bg-warning/20 text-warning">默认</Badge>
          )}
        </div>

        <div className="flex justify-between items-center mt-auto">
          <span className="text-xs text-muted-foreground">
            使用次数: {template.usage_count || 0}
          </span>
          <span className="text-xs text-muted-foreground">
            {new Date(template.updated_at).toLocaleDateString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">
          Template Manager
          {entityType && ` - ${templateService.getEntityDisplayName(entityType)}`}
        </h2>

        <Button
          variant={dialogMode ? 'outline' : 'default'}
          onClick={() => setUploadDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload Template
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="relative min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {!entityType && (
          <Select
            value={formData.entityType}
            onValueChange={(value) => setFormData(prev => ({ ...prev, entityType: value }))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sales">销售订单</SelectItem>
              <SelectItem value="quotation">报价单</SelectItem>
              <SelectItem value="purchase">采购订单</SelectItem>
              <SelectItem value="warehouse_inbound">入库单</SelectItem>
              <SelectItem value="warehouse_outbound">出库单</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select
          value={selectedLanguage || 'all'}
          onValueChange={(value) => setSelectedLanguage(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="cn">中文</SelectItem>
            <SelectItem value="en">English</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            No templates found. Upload your first template to get started.
          </p>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Upload Template
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {templates.map((template) => (
              <TemplateCard key={template._id} template={template} />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(parseInt(value, 10));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Template Annotator Dialog */}
      {annotatorTemplate && (
        <TemplateAnnotator
          open={annotatorOpen}
          onClose={() => {
            setAnnotatorOpen(false);
            setAnnotatorTemplate(null);
          }}
          template={annotatorTemplate}
          onSave={handleAnnotatorSave}
        />
      )}

      {/* Manual Template Mapper Dialog */}
      {manualMapperTemplate && (
        <ManualTemplateMapper
          open={manualMapperOpen}
          onClose={() => {
            setManualMapperOpen(false);
            setManualMapperTemplate(null);
          }}
          template={manualMapperTemplate}
          onSave={handleManualMapperSave}
        />
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Template</DialogTitle>
            <DialogDescription>Upload a new Excel template file</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {formErrors.submit && (
              <Alert variant="destructive">
                <AlertDescription>{formErrors.submit}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={formErrors.name ? 'border-red-500' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-destructive">{formErrors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-id">Template ID</Label>
              <Input
                id="template-id"
                value={formData.templateId}
                onChange={(e) => setFormData(prev => ({ ...prev, templateId: e.target.value }))}
                className={formErrors.templateId ? 'border-red-500' : ''}
              />
              <p className="text-xs text-muted-foreground">
                {formErrors.templateId || 'Unique identifier for this template'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-desc">Description (Optional)</Label>
              <Textarea
                id="template-desc"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select
                  value={formData.entityType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, entityType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">销售订单</SelectItem>
                    <SelectItem value="quotation">报价单</SelectItem>
                    <SelectItem value="purchase">采购订单</SelectItem>
                    <SelectItem value="warehouse_inbound">入库单</SelectItem>
                    <SelectItem value="warehouse_outbound">出库单</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cn">中文</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-file">Template File</Label>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => document.getElementById('template-file-input').click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadFile ? uploadFile.name : 'Select Excel File (.xlsx, .xls)'}
              </Button>
              <input
                id="template-file-input"
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
              {formErrors.file && (
                <p className="text-sm text-destructive">{formErrors.file}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace File Dialog */}
      <Dialog open={replaceFileDialogOpen} onOpenChange={setReplaceFileDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Replace Template File</DialogTitle>
            <DialogDescription>
              Upload a new file for "{replaceFileTemplate?.name}". The metadata and mappings will be preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {replaceFormErrors.submit && (
              <Alert variant="destructive">
                <AlertDescription>{replaceFormErrors.submit}</AlertDescription>
              </Alert>
            )}

            <div className="p-3 bg-muted rounded-lg">
              <div className="text-sm">
                <span className="font-medium">Template ID:</span> {replaceFileTemplate?.template_id}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                <span className="font-medium">Entity Type:</span> {templateService.getEntityDisplayName(replaceFileTemplate?.entity_type)}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="replace-file">New Template File</Label>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => document.getElementById('replace-file-input').click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                {replaceFile ? replaceFile.name : 'Select Excel File (.xlsx, .xls)'}
              </Button>
              <input
                id="replace-file-input"
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => setReplaceFile(e.target.files[0])}
              />
              {replaceFormErrors.file && (
                <p className="text-sm text-destructive">{replaceFormErrors.file}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="change-notes">Change Notes (Optional)</Label>
              <Textarea
                id="change-notes"
                rows={2}
                placeholder="Describe what changed in this version..."
                value={replaceChangeNotes}
                onChange={(e) => setReplaceChangeNotes(e.target.value)}
              />
            </div>

            <Alert>
              <AlertDescription className="text-sm">
                This will create a new version of the template. Your metadata configuration and field mappings will be preserved.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceFileDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReplaceFile} disabled={submitting || !replaceFile}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Replace File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateManager;
